/**
 * PersonnelOrderActions — Boutons de validation CAS PAR CAS pour un bon personnel.
 *
 * Affiche 3 actions admin sur un bon en attente (`pending_manager` / `pending_director`) :
 *  - Autoriser (prix normal, 0% de remise)
 *  - Avec remise…  (slider 0-100%)
 *  - Refuser        (motif obligatoire)
 *
 * Utilisé en 2 endroits :
 *  - `PersonnelAgentRecap.jsx` : dans la modale de détail par agent (déjà en place)
 *  - `EmployeeOrdersTab.jsx` / `ManagerOrdersTab.jsx` : directement sur chaque ligne de bon
 *
 * Props :
 *  - order          : le bon (`{ id, employee_name, subtotal, items, status, ... }`)
 *  - scope          : "employee" | "manager"
 *  - adminName      : nom du valideur (audit)
 *  - onRefresh      : callback pour rafraîchir la liste après action
 *  - variant        : "inline" (compact, 1 seule ligne) | "block" (empilé)
 */
import React, { useState } from "react";
import axios from "axios";
import { ThumbsUp, Percent, Ban, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PersonnelOrderActions = ({ order, scope = "employee", adminName = "Admin", onRefresh, variant = "inline" }) => {
  const [busy, setBusy] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountRate, setDiscountRate] = useState(50);
  const [discountReason, setDiscountReason] = useState("");
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");

  const isPending = order?.status === "pending_manager" || order?.status === "pending_director";
  const isCancelled = order?.status === "cancelled";
  // 🆕 Boutons visibles pour bons en attente ET bons refusés (ré-autorisation possible)
  if (!isPending && !isCancelled) return null;

  const fmt = (v) => Number(v || 0).toLocaleString("fr-FR");
  const sub = Number(order.subtotal || 0);
  const disc = Math.round(sub * (discountRate / 100));
  const finalT = sub - disc;

  const authorizeAtRate = async (rate, reason = "") => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/personnel-orders/authorize-single`, {
        scope, order_id: order.id, discount_rate: rate, custom_reason: reason, admin_name: adminName,
      });
      const label = rate === 0 ? "prix normal" : `remise ${Math.round(rate * 100)}%`;
      toast.success(`Bon autorisé (${label})`);
      setDiscountOpen(false); setDiscountReason(""); setDiscountRate(50);
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur autorisation");
    } finally { setBusy(false); }
  };

  const refuseNow = async () => {
    const reason = (refuseReason || "").trim();
    if (reason.length < 3) { toast.error("Motif obligatoire (min. 3 caractères)"); return; }
    setBusy(true);
    try {
      const endpoint = scope === "manager" ? "manager-orders" : "employee-orders";
      await axios.delete(`${API}/${endpoint}/${order.id}`, { params: { reason, by_role: "admin" } });
      toast.success("Bon refusé");
      setRefuseOpen(false); setRefuseReason("");
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur refus");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div
        className={variant === "block" ? "flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-amber-500/20" : "flex flex-wrap gap-1.5"}
        data-testid={`personnel-order-actions-${order.id}`}
      >
        <Button
          size="sm"
          disabled={busy}
          onClick={() => authorizeAtRate(0)}
          className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`inline-authorize-full-${order.id}`}
          title={isCancelled ? "Ré-autoriser au prix normal (annule le refus précédent)" : "Autoriser au prix normal (aucune remise)"}
        >
          <ThumbsUp className="w-3 h-3 mr-1" /> {isCancelled ? "Ré-autoriser" : "Autoriser"}
        </Button>
        <Button
          size="sm"
          disabled={busy}
          onClick={() => { setDiscountOpen(true); setDiscountRate(50); setDiscountReason(""); }}
          className="h-7 px-2 text-[11px] bg-blue-600 hover:bg-blue-700 text-white"
          data-testid={`inline-authorize-discount-${order.id}`}
          title="Autoriser avec un taux de remise personnalisé (0-100%)"
        >
          <Percent className="w-3 h-3 mr-1" /> Avec remise
        </Button>
        {/* "Refuser" caché si déjà refusé (bon cancelled) */}
        {!isCancelled && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => { setRefuseOpen(true); setRefuseReason(""); }}
            className="h-7 px-2 text-[11px] bg-red-600 hover:bg-red-700 text-white"
            data-testid={`inline-refuse-${order.id}`}
            title="Refuser ce bon (motif obligatoire)"
          >
            <Ban className="w-3 h-3 mr-1" /> Refuser
          </Button>
        )}
      </div>

      {/* Modale REMISE */}
      <Dialog open={discountOpen} onOpenChange={(v) => { if (!v) { setDiscountOpen(false); setDiscountReason(""); } }}>
        <DialogContent
          className="bg-slate-900 border-blue-500/50 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 32rem)" }}
          data-testid={`inline-discount-modal-${order.id}`}
        >
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2 text-lg">
              <Percent className="w-5 h-5" />
              Autoriser avec remise
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 text-[12px]">
              <p className="text-slate-300"><b>{order.employee_name}</b> · {(order.items || []).map(i => `${i.quantity}× ${i.name}`).join(" · ") || "—"}</p>
              <p className="text-slate-500 mt-1">Prix normal : <span className="text-white font-bold tabular-nums">{fmt(sub)} F</span></p>
            </div>
            <div>
              <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">
                Taux de remise : <span className="text-blue-300 font-black">{discountRate}%</span>
              </label>
              <input
                type="range" min="0" max="100" step="5"
                value={discountRate}
                onChange={(e) => setDiscountRate(parseInt(e.target.value, 10))}
                className="w-full mt-2 accent-blue-500"
                data-testid={`inline-discount-slider-${order.id}`}
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                <span>0% (prix normal)</span>
                <span>50%</span>
                <span>100% (gratuit)</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-[12px]">
              <div className="rounded bg-slate-800/50 border border-slate-700 p-2">
                <p className="text-slate-500 text-[10px] uppercase">Prix</p>
                <p className="text-white font-bold tabular-nums">{fmt(sub)} F</p>
              </div>
              <div className="rounded bg-blue-500/10 border border-blue-500/30 p-2">
                <p className="text-blue-300 text-[10px] uppercase">Remise</p>
                <p className="text-blue-200 font-bold tabular-nums">-{fmt(disc)} F</p>
              </div>
              <div className="rounded bg-emerald-500/10 border border-emerald-500/30 p-2">
                <p className="text-emerald-300 text-[10px] uppercase">À retenir</p>
                <p className="text-emerald-200 font-black tabular-nums">{fmt(finalT)} F</p>
              </div>
            </div>
            <div>
              <label className="text-slate-300 text-xs">Motif (optionnel, archivé)</label>
              <input
                type="text"
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder={`Ex : remise exceptionnelle ${discountRate}% validée`}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-blue-500/60 focus:outline-none"
                data-testid={`inline-discount-reason-${order.id}`}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-slate-700">
              <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setDiscountOpen(false); setDiscountReason(""); }} data-testid={`inline-discount-cancel-${order.id}`}>
                Annuler
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                onClick={() => authorizeAtRate(discountRate / 100, discountReason)}
                disabled={busy}
                data-testid={`inline-discount-confirm-${order.id}`}
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Autoriser à {fmt(finalT)} F
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale REFUS */}
      <Dialog open={refuseOpen} onOpenChange={(v) => { if (!v) { setRefuseOpen(false); setRefuseReason(""); } }}>
        <DialogContent
          className="bg-slate-900 border-red-500/50 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 32rem)" }}
          data-testid={`inline-refuse-modal-${order.id}`}
        >
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2 text-lg">
              <Ban className="w-5 h-5" />
              Refuser ce bon
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 text-[12px]">
              <p className="text-slate-300"><b>{order.employee_name}</b> · {(order.items || []).map(i => `${i.quantity}× ${i.name}`).join(" · ") || "—"}</p>
              <p className="text-slate-500 mt-1">Prix normal dû : <span className="text-red-300 font-bold tabular-nums">{fmt(sub)} F</span></p>
            </div>
            <div>
              <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">
                Motif de refus <span className="text-red-400">*</span>
                <span className="text-slate-500 normal-case ml-1">(min. 3 caractères, archivé)</span>
              </label>
              <textarea
                value={refuseReason}
                onChange={(e) => setRefuseReason(e.target.value)}
                placeholder="Ex : dépassement du plafond mensuel, hors périmètre de la remise personnel, etc."
                rows={3}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-red-500/60 focus:outline-none"
                data-testid={`inline-refuse-reason-${order.id}`}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-slate-700">
              <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setRefuseOpen(false); setRefuseReason(""); }} data-testid={`inline-refuse-cancel-${order.id}`}>
                Annuler
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white font-bold"
                onClick={refuseNow}
                disabled={busy || refuseReason.trim().length < 3}
                data-testid={`inline-refuse-confirm-${order.id}`}
              >
                <Ban className="w-4 h-4 mr-1" /> Confirmer le refus
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PersonnelOrderActions;
